// MessageStack — how messages look (pen "Messages" §1), pure. The pieces the
// shell's MessageLayer composes with its queue (app/messageQueue.ts):
//
//   - MessageToast: one line (title, an optional second line, a "×N" count,
//     an action, close). Ink ground; a problem wears a light ground with a
//     danger edge.
//   - MessageMore: the "+N more" chip above the stack (or "Show fewer").
//   - LastStepLine: the quiet "✓ Added as part 3" pill beside the bottom row.
//   - ObjectNote: a problem with one object, shown at it: the object flashes
//     (accent-soft + agent edge) and a small label sits at the drop spot.
//
// Props in, intents out; no state, no timers (the queue owns the timing).

export type { Rect };
export { LastStepLine, MessageMore, MessageToast, ObjectNote };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./MessageStack.css";

type MessageTone = "neutral" | "success" | "warning" | "danger";

// The tone's redundant icon (the tone never rides on colour alone).
const toneIcon: Record<MessageTone, ReactNode> = {
    neutral: <Icon name="info" />,
    success: <Icon name="check" />,
    warning: <Icon name="warning" />,
    danger: <Icon name="danger" />,
};

const MessageToast = ({
    tone,
    title,
    description,
    count,
    action,
    onClose,
}: {
    readonly tone: MessageTone;
    readonly title: string;
    readonly description: string;
    // How many times it arrived; shown as "×N" from 2.
    readonly count: number;
    readonly action?: ReactNode;
    readonly onClose: () => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div className={`bilda-message bilda-message--${tone}`}>
            <span className="bilda-message__icon" aria-hidden="true">
                {toneIcon[tone]}
            </span>
            <div className="bilda-message__text">
                <span className="bilda-message__title">{title}</span>
                {description === "" ? null : (
                    <span className="bilda-message__description">
                        {description}
                    </span>
                )}
            </div>
            {count > 1 ? (
                <span className="bilda-message__count">
                    <span aria-hidden="true">{`×${count}`}</span>
                    <VisuallyHidden>{t`${count} times`}</VisuallyHidden>
                </span>
            ) : null}
            {action === undefined ? null : (
                <div className="bilda-message__action">{action}</div>
            )}
            <button
                type="button"
                className="bilda-message__close"
                aria-label={t`Dismiss`}
                onClick={onClose}
            >
                <Icon name="close" />
            </button>
        </div>
    );
};

const MessageMore = ({
    label,
    onClick,
}: {
    readonly label: string;
    readonly onClick: () => void;
}): ReactNode => (
    <button type="button" className="bilda-message-more" onClick={onClick}>
        {label}
    </button>
);

const LastStepLine = ({
    text,
    action,
    left,
    bottom,
    onHold,
    onRelease,
}: {
    readonly text: string;
    readonly action?: ReactNode;
    readonly left: number;
    readonly bottom: number;
    // Hover or focus holds it (its timer pauses); leaving lets it go.
    readonly onHold: () => void;
    readonly onRelease: () => void;
}): ReactNode => (
    <div
        className="bilda-last-step"
        role="status"
        style={{ left, bottom }}
        onMouseEnter={onHold}
        onMouseLeave={onRelease}
        onFocus={onHold}
        onBlur={onRelease}
    >
        <Icon name="check" />
        <span>{text}</span>
        {action === undefined ? null : (
            <span className="bilda-last-step__action">{action}</span>
        )}
    </div>
);

// Viewport pixels.
type Rect = {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
};

const ObjectNote = ({
    object,
    flash,
    label,
}: {
    // The object the note is about (the label sits centred below it).
    readonly object: Rect;
    // What flashes: the object itself or one part inside it.
    readonly flash: Rect;
    readonly label: string;
}): ReactNode => (
    <>
        <div
            className="bilda-object-note__flash"
            aria-hidden="true"
            style={{
                left: flash.left,
                top: flash.top,
                width: flash.width,
                height: flash.height,
            }}
        />
        <div
            className="bilda-object-note__label"
            role="status"
            style={{
                left: object.left + object.width / 2,
                top: object.top + object.height,
            }}
        >
            <Icon name="info" />
            <span>{label}</span>
        </div>
    </>
);
